import { Tab, TabList, TabPanel, Tabs } from '@atlas/ui-primitives'
import type { ComponentType, Key } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMappingStatus } from '@/hooks/useWorkfrontMappings'
import { formatUtc } from '@/lib/format-utc'
import { WorkfrontHeader } from '../shared'
import { AssetTypesTab } from './AssetTypesTab'
import { DraftControls } from './DraftControls'
import { FieldsTab } from './FieldsTab'
import { HistoryTab } from './HistoryTab'
import { LookupsTab } from './LookupsTab'
import { MAPPINGS_PATHS, MAPPINGS_TAB_LABELS, MappingsTab } from './paths'
import { useActorName, useReportLoadFailure } from './tab-support'
import { UnmappedFieldsTab } from './UnmappedFieldsTab'
import { ValuesTab } from './ValuesTab'

const TAB_CONTENT: Record<MappingsTab, ComponentType> = {
	[MappingsTab.FIELDS]: FieldsTab,
	[MappingsTab.VALUES]: ValuesTab,
	[MappingsTab.ASSET_TYPES]: AssetTypesTab,
	[MappingsTab.LOOKUPS]: LookupsTab,
	[MappingsTab.UNMAPPED_FIELDS]: UnmappedFieldsTab,
	[MappingsTab.HISTORY]: HistoryTab,
}

const isTab = (value: string | undefined): value is MappingsTab =>
	(Object.values(MappingsTab) as string[]).includes(value ?? '')

/** The published version line under the heading: which version, by whom, when. */
function PublishedLine() {
	const status = useMappingStatus()
	const nameOf = useActorName()
	useReportLoadFailure(status.isError, 'mapping status')
	const latest = status.data?.latest
	if (!latest) return null
	return (
		<p className="text-ds-sm text-ds-text-muted">
			Published version {latest.version} by {nameOf(latest.publishedBy)}, {formatUtc(latest.publishedAt)}
		</p>
	)
}

/**
 * Admin › Workfront › Mappings: what Workfront may set in ATLAS, as six tabs that
 * share the draft bar. Tabs are a keyboard-operable tab list bound to the URL, so
 * every tab (and an error's Fix link into one) has its own address.
 */
export function WorkfrontMappingsPage() {
	const { tab } = useParams()
	const navigate = useNavigate()
	const status = useMappingStatus()
	if (!isTab(tab)) return <Navigate to={MAPPINGS_PATHS.tab(MappingsTab.FIELDS)} replace />
	const Content = TAB_CONTENT[tab]
	return (
		<div className="space-y-6">
			<WorkfrontHeader title="Mappings" />
			<PublishedLine />
			<DraftControls draft={status.data?.draft ?? null} />
			<Tabs selectedKey={tab} onSelectionChange={(key: Key) => void navigate(MAPPINGS_PATHS.tab(key as MappingsTab))}>
				<TabList aria-label="Mapping tabs" variant="underline">
					{Object.values(MappingsTab).map((id) => (
						<Tab key={id} id={id}>
							{MAPPINGS_TAB_LABELS[id]}
						</Tab>
					))}
				</TabList>
				<TabPanel id={tab} className="pt-4">
					<Content />
				</TabPanel>
			</Tabs>
		</div>
	)
}
